/**
 * HeyGen Avatar Studio — Avatars parlants Ralph + Sonia (vraies photos + vraies voix clonées).
 * Route admin : /app/heygen
 *
 * Différence avec /app/avatar-studio :
 *  - Ici = avatar PARLANT (HeyGen) : photo + voix clonée → vidéo qui parle ton texte
 *  - Là-bas = b-roll IA (Sora 2) + tes selfies bruts (sans IA)
 */
import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mic, Video, Loader2, Sparkles, Trash2, RefreshCw, Upload, Play } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const USE_CASES = [
  { id: "cours_en_ligne", label: "📚 Cours en ligne" },
  { id: "powerkick_her", label: "🥊 PowerKick Her" },
  { id: "camille_receptionist", label: "📞 Camille AI" },
  { id: "marketing_general", label: "📣 Marketing général" },
];

const FORMATS = [
  { id: "vertical", label: "📱 Vertical (Reels/TikTok)", w: 720, h: 1280 },
  { id: "horizontal", label: "🖥️ Horizontal (YouTube/Web)", w: 1280, h: 720 },
  { id: "square", label: "⬜ Carré (Insta feed)", w: 1080, h: 1080 },
];

export default function HeyGenAvatarModule() {
  const [personas, setPersonas] = useState([]);
  const [clips, setClips] = useState([]);
  const [tab, setTab] = useState("generate");
  const [loading, setLoading] = useState(false);

  // Generate form
  const [genForm, setGenForm] = useState({
    persona_key: "narrateur_kaizen",  // Par défaut = porte-parole pro Kaizen
    script: "",
    use_case: "marketing_general",
    title: "",
    format: "vertical",
  });
  const [generating, setGenerating] = useState(false);

  // Recreate avatar/voice
  const photoRef = useRef(null);
  const audioRef = useRef(null);
  const [recreateTarget, setRecreateTarget] = useState("kru_ralph");
  const [recreateName, setRecreateName] = useState("");
  const [recreating, setRecreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/heygen/personas"), api.get("/heygen/clips")]);
      setPersonas(p.data.personas);
      setClips(c.data.clips);
    } catch { toast.error("Erreur chargement"); }
    setLoading(false);
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const generate = async () => {
    if (!genForm.script.trim()) return toast.error("Tape un texte à dire");
    if (genForm.script.length > 1500) return toast.error("Texte trop long (max 1500 caractères)");
    setGenerating(true);
    const fmt = FORMATS.find(f => f.id === genForm.format);
    try {
      await api.post("/heygen/generate", {
        persona_key: genForm.persona_key,
        script: genForm.script,
        use_case: genForm.use_case,
        title: genForm.title || undefined,
        width: fmt.w,
        height: fmt.h,
      });
      toast.success("🎬 Vidéo en génération ! 2-5 min — actualisation auto toutes les 15s.");
      setGenForm({ ...genForm, script: "", title: "" });
      setTab("library");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération");
    }
    setGenerating(false);
  };

  const recreateAvatar = async () => {
    if (!photoRef.current?.files?.[0]) return toast.error("Sélectionne une photo");
    if (!recreateName.trim()) return toast.error("Nom requis");
    setRecreating(true);
    try {
      const fd = new FormData();
      fd.append("persona_key", recreateTarget);
      fd.append("name", recreateName);
      fd.append("image", photoRef.current.files[0]);
      await api.post("/heygen/avatars/recreate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nouvel avatar créé ! Les prochaines vidéos l'utiliseront.");
      if (photoRef.current) photoRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur recréation");
    }
    setRecreating(false);
  };

  const cloneVoice = async () => {
    if (!audioRef.current?.files?.[0]) return toast.error("Sélectionne un fichier audio");
    if (!recreateName.trim()) return toast.error("Nom requis");
    setRecreating(true);
    try {
      const fd = new FormData();
      fd.append("persona_key", recreateTarget);
      fd.append("voice_name", recreateName);
      fd.append("audio", audioRef.current.files[0]);
      await api.post("/heygen/voices/clone", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nouvelle voix clonée ! Les prochaines vidéos l'utiliseront.");
      if (audioRef.current) audioRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur clonage");
    }
    setRecreating(false);
  };

  const deleteClip = async (id) => {
    if (!window.confirm("Supprimer ce clip ?")) return;
    try {
      await api.delete(`/heygen/clip/${id}`);
      toast.success("Supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="heygen-module">
      <div>
        <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
          <Mic className="w-7 h-7 text-amber-400" /> Avatar parlant HeyGen
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          Ta vraie photo + ta vraie voix clonée → tu tapes un texte → vidéo qui parle. <strong>Pas de mismatch</strong>.
          Ralph parle masculin · Sonia parle féminin.
        </p>
      </div>

      {/* Personas Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {personas.map(p => (
          <div key={p.persona_key} className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-zinc-950 p-4" data-testid={`persona-${p.persona_key}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-md font-semibold text-amber-100">{p.display_name}</div>
                <div className="text-xs text-zinc-400 mt-1">
                  {p.gender === "male" ? "♂" : "♀"} · {p.language} · {p.use_cases.length} use cases
                </div>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px]">✓ prêt</span>
            </div>
            <div className="text-[10px] text-zinc-500 mt-3 font-mono break-all">
              avatar: {p.avatar_id?.substring(0, 16)}... · voice: {p.voice_id?.substring(0, 16)}...
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { id: "generate", label: "🎬 Générer vidéo" },
          { id: "library", label: `📚 Bibliothèque (${clips.length})` },
          { id: "recreate", label: "🔄 Refaire avatar / voix" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-400 hover:text-amber-200"
            }`} data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* GENERATE */}
      {tab === "generate" && (
        <div className="space-y-4 max-w-2xl">
          <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-zinc-950 p-5">
            <h3 className="text-md font-semibold text-purple-200 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Crée ta vidéo parlante
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Qui parle ?</label>
                <select value={genForm.persona_key} onChange={(e) => setGenForm({ ...genForm, persona_key: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="gen-persona">
                  {personas.map(p => <option key={p.persona_key} value={p.persona_key}>{p.display_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Texte à dire (jusqu'à 1500 caractères, soit ~1 min)</label>
                <textarea placeholder="Ex: Salut, je suis Kru Ralph. Bienvenue au dojo Impact Kick-boxing/Jiu-jitsu. Si tu veux essayer un cours d'essai (19,98 $), clique le lien dans ma bio..."
                  value={genForm.script} maxLength={1500}
                  onChange={(e) => setGenForm({ ...genForm, script: e.target.value })}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100 min-h-[140px]" data-testid="gen-script" />
                <div className="text-[10px] text-zinc-500 text-right mt-1">{genForm.script.length} / 1500</div>
              </div>
              <input placeholder="Titre du clip (optionnel)" value={genForm.title}
                onChange={(e) => setGenForm({ ...genForm, title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="gen-title" />
              <div className="grid grid-cols-2 gap-2">
                <select value={genForm.format} onChange={(e) => setGenForm({ ...genForm, format: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-amber-100" data-testid="gen-format">
                  {FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <select value={genForm.use_case} onChange={(e) => setGenForm({ ...genForm, use_case: e.target.value })}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-amber-100" data-testid="gen-usecase">
                  {USE_CASES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </div>
              <button onClick={generate} disabled={generating || !genForm.script.trim()}
                className="w-full px-4 py-3 rounded-lg bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-zinc-950 font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="generate-btn">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi...</> : <><Sparkles className="w-4 h-4" /> Générer (2-5 min)</>}
              </button>
              <div className="text-xs text-zinc-500 italic">
                💡 Astuce : ~1$ USD / minute générée. 30 sec ≈ 0,50$.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LIBRARY */}
      {tab === "library" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-semibold text-amber-200">Tes vidéos parlantes</h3>
            <button onClick={load} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1" data-testid="refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </button>
          </div>
          {clips.length === 0 && !loading && (
            <div className="text-center text-zinc-500 py-12">
              <Video className="w-10 h-10 mx-auto mb-3 opacity-50" />
              Aucune vidéo. Va dans "Générer vidéo" pour créer ta première.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clips.map(c => (
              <div key={c.id} className="rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden" data-testid={`clip-${c.id}`}>
                {c.status === "completed" && c.file_url ? (
                  <video controls preload="metadata" className="w-full bg-zinc-900" style={{ aspectRatio: c.width && c.height ? `${c.width}/${c.height}` : "16/9" }}>
                    <source src={`${BACKEND}${c.file_url}`} type="video/mp4" />
                  </video>
                ) : (
                  <div className="w-full h-48 bg-zinc-900 flex items-center justify-center" style={{ aspectRatio: c.width && c.height ? `${c.width}/${c.height}` : "16/9" }}>
                    {c.status === "processing" ? (
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-400 mx-auto mb-2" />
                        <div className="text-xs text-zinc-400">Génération...</div>
                      </div>
                    ) : (
                      <div className="text-center text-rose-400 text-xs px-3">
                        ❌ Échec<br />
                        <span className="text-zinc-500">{c.failure_message || "voir logs"}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-amber-100 truncate">{c.title}</div>
                    <button onClick={() => deleteClip(c.id)} className="text-zinc-500 hover:text-rose-400" data-testid={`del-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{c.persona_name}</span>
                    <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">{c.use_case}</span>
                    {c.duration_sec && <span>{c.duration_sec.toFixed(1)}s</span>}
                  </div>
                  <p className="text-xs text-zinc-400 mt-2 italic line-clamp-2">"{c.script}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* RECREATE */}
      {tab === "recreate" && (
        <div className="space-y-4 max-w-2xl">
          <div className="text-xs text-zinc-400 bg-zinc-900/60 rounded-lg p-3 border border-zinc-800">
            💡 Tu n'aimes pas le rendu d'une voix ou d'une photo ? Téléverse une nouvelle version ici.
            <strong className="text-amber-300"> L'ancienne reste dans ton compte HeyGen</strong>, tu peux la supprimer manuellement plus tard sur heygen.com.
          </div>

          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Pour quel persona ?</label>
            <select value={recreateTarget} onChange={(e) => setRecreateTarget(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="recreate-target">
              {personas.map(p => <option key={p.persona_key} value={p.persona_key}>{p.display_name}</option>)}
            </select>
          </div>
          <input placeholder="Nom (ex: Ralph_v2_kimono ou Sonia_v3_gym)" value={recreateName}
            onChange={(e) => setRecreateName(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100" data-testid="recreate-name" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-zinc-950 p-4">
              <h3 className="text-md font-semibold text-blue-200 mb-2 flex items-center gap-2">
                <Upload className="w-4 h-4" /> Nouvelle photo (avatar)
              </h3>
              <input type="file" ref={photoRef} accept="image/jpeg,image/png"
                className="w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-500 file:text-zinc-950 file:font-semibold"
                data-testid="recreate-photo" />
              <button onClick={recreateAvatar} disabled={recreating}
                className="w-full mt-3 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-zinc-950 font-semibold text-xs"
                data-testid="recreate-photo-btn">
                {recreating ? "Création..." : "Créer nouvel avatar"}
              </button>
            </div>
            <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-zinc-950 p-4">
              <h3 className="text-md font-semibold text-emerald-200 mb-2 flex items-center gap-2">
                <Mic className="w-4 h-4" /> Nouvelle voix (clone)
              </h3>
              <input type="file" ref={audioRef} accept="audio/mpeg,audio/mp3,audio/wav,audio/m4a"
                className="w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-500 file:text-zinc-950 file:font-semibold"
                data-testid="recreate-audio" />
              <button onClick={cloneVoice} disabled={recreating}
                className="w-full mt-3 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-950 font-semibold text-xs"
                data-testid="recreate-voice-btn">
                {recreating ? "Clonage..." : "Cloner nouvelle voix"}
              </button>
              <div className="text-[10px] text-zinc-500 mt-2 italic">
                Conseil : enregistre 30-60 sec dans un endroit calme, voix naturelle.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
